"use client"

import { useState, useEffect } from 'react'
import { useRouter } from 'next/navigation'
import { Logo } from '@/components/logo'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { AuthBackground } from '@/components/ui/auth-background'
import { DotPattern } from '@/components/magicui/dot-pattern'
import { useMutation } from 'convex/react'
import { api } from 'socialloop-convex/api'
import { toast } from 'sonner'
import confetti from 'canvas-confetti'
import { 
  Calendar, 
  Users, 
  MapPin, 
  Clock, 
  Sparkles, 
  ArrowRight,
  CheckCircle,
  Zap,
  Target,
  Gamepad2,
  Music,
  Briefcase,
  GraduationCap,
  Palette,
  Mic
} from 'lucide-react'
import Link from 'next/link'

interface EventFormData {
  name: string
  description: string
  eventType: string
  date: string
  time: string
  location: string
  maxAttendees: string
  isPublic: boolean
}

interface FormErrors {
  name?: string
  description?: string
  eventType?: string
  date?: string
  time?: string
  location?: string
  maxAttendees?: string
}

export default function GettingStartedPage() {
  const router = useRouter()
  const createEvent = useMutation(api.events.create)
  const [currentStep, setCurrentStep] = useState(1)
  const [isLoading, setIsLoading] = useState(false)
  const [errors, setErrors] = useState<FormErrors>({})
  
  const [formData, setFormData] = useState<EventFormData>({
    name: '',
    description: '',
    eventType: '',
    date: '',
    time: '',
    location: '',
    maxAttendees: '50',
    isPublic: true
  })

  const eventTypes = [
    { value: 'networking', label: 'Networking Event', icon: Users, color: 'bg-[#1e74e6]' },
    { value: 'conference', label: 'Conference', icon: Briefcase, color: 'bg-[#17cbbf]' },
    { value: 'workshop', label: 'Workshop', icon: GraduationCap, color: 'bg-[#60a5fa]' },
    { value: 'social', label: 'Social Gathering', icon: Music, color: 'bg-[#facc15]' },
    { value: 'meetup', label: 'Community Meetup', icon: Target, color: 'bg-[#1e74e6]' },
    { value: 'presentation', label: 'Presentation', icon: Mic, color: 'bg-[#17cbbf]' },
    { value: 'creative', label: 'Creative Event', icon: Palette, color: 'bg-[#60a5fa]' },
    { value: 'gaming', label: 'Gaming Event', icon: Gamepad2, color: 'bg-[#facc15]' }
  ]

  const updateFormData = (field: keyof EventFormData, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    // Clear error when user starts typing
    if (errors[field as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [field]: undefined }))
    }
  }

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {}
    
    if (!formData.name.trim()) newErrors.name = 'Event name is required'
    if (!formData.description.trim()) newErrors.description = 'Event description is required'
    if (!formData.eventType) newErrors.eventType = 'Please select an event type'
    if (!formData.date) newErrors.date = 'Event date is required'
    if (!formData.time) newErrors.time = 'Event time is required'
    if (!formData.location.trim()) newErrors.location = 'Event location is required'
    
    const maxAttendees = parseInt(formData.maxAttendees)
    if (isNaN(maxAttendees) || maxAttendees < 1) {
      newErrors.maxAttendees = 'Please enter a valid number of attendees'
    }
    
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const triggerSuccessConfetti = () => {
    const colors = ['#1e74e6', '#17cbbf', '#60a5fa', '#facc15']
    
    // Center burst
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 },
      colors: colors
    })
    
    // Side bursts
    setTimeout(() => {
      confetti({
        particleCount: 50,
        spread: 60,
        origin: { y: 0.7, x: 0.2 },
        colors: colors
      })
      confetti({
        particleCount: 50,
        spread: 60,
        origin: { y: 0.7, x: 0.8 },
        colors: colors
      })
    }, 250)
  }

  const handleCreateEvent = async () => {
    if (!validateForm()) return

    setIsLoading(true)

    try {
      // Combine date and time into startTime and endTime
      const startDateTime = new Date(`${formData.date}T${formData.time}`)
      const endDateTime = new Date(startDateTime.getTime() + 2 * 60 * 60 * 1000) // Add 2 hours

      await createEvent({
        name: formData.name,
        description: formData.description || undefined,
        eventType: formData.eventType,
        startTime: startDateTime.getTime(),
        endTime: endDateTime.getTime(),
        location: formData.location,
        maxAttendees: parseInt(formData.maxAttendees),
        isPublic: formData.isPublic,
      })

      triggerSuccessConfetti()
      toast.success('🎉 Your first event has been created!')

      // Redirect to dashboard after a short delay
      setTimeout(() => {
        router.push('/dashboard')
      }, 2000)
    } catch (error) {
      console.error('Error creating event:', error)
      toast.error('Failed to create event. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  // Get today's date for min date validation
  const today = new Date().toISOString().split('T')[0]

  return (
    <>
      {/* Background Effects */}
      <DotPattern
        width={24}
        height={24}
        cx={1}
        cy={1}
        cr={1}
        glow={false}
        className="fill-neutral-400/15 [mask-image:radial-gradient(800px_circle_at_center,white,transparent)]"
      />

      <AuthBackground
        spotlightIntensity="subtle"
        focusPosition="center"
        animationSpeed="slow"
        colorScheme="purple-blue"
        enableFormFocus={true}
        enableAccentGradients={false}
      />

      <section className="relative min-h-screen bg-white will-change-transform">
        <div className="container mx-auto px-4 py-8 flex flex-col justify-center min-h-screen">
          {/* Header with Logo */}
          <div className="mb-8 text-center">
            <Link href="/" aria-label="go home" className="inline-block mb-8">
              <div className="bg-[#facc15] border-4 border-black p-4 inline-block transform rotate-1 hover:rotate-0 transition-transform duration-200">
                <Logo className="h-8 w-auto text-black" uniColor />
              </div>
            </Link>
            
            <div className="bg-[#1e74e6] border-8 border-black p-6 lg:p-8 transform -rotate-1 hover:rotate-0 transition-transform duration-200 max-w-3xl mx-auto">
              <h1 className="text-4xl lg:text-6xl font-black text-white mb-4 uppercase tracking-tight">
                CREATE YOUR<br />FIRST EVENT!
              </h1>
              <p className="text-lg lg:text-2xl font-bold text-white">
                LET'S GET YOUR EVENT HOSTING JOURNEY STARTED!
              </p>
            </div>
          </div>

          {/* Main Form Card */}
          <div className="max-w-4xl mx-auto w-full">
            <div className="bg-[#17cbbf] border-8 border-black p-8 transform rotate-1 hover:rotate-0 transition-transform duration-200">
              <div className="bg-white border-4 border-black p-8 transform -rotate-1">
                <div className="space-y-8">
                  {/* Progress Indicator */}
                  <div className="flex items-center justify-center mb-8">
                    <div className="bg-[#60a5fa] border-4 border-black p-4 transform rotate-2">
                      <div className="flex items-center gap-2">
                        <Sparkles className="w-6 h-6 text-black" />
                        <span className="text-xl font-black text-black uppercase">
                          STEP 1: EVENT BASICS
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Event Name */}
                  <div className="space-y-3">
                    <Label htmlFor="name" className="text-xl font-black text-black uppercase tracking-wide">
                      EVENT NAME
                    </Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => updateFormData('name', e.target.value)}
                      placeholder="My Awesome Networking Event"
                      className={`border-4 border-black bg-white font-bold text-black placeholder:text-black/60 focus:bg-[#facc15] text-lg p-4 h-14 ${
                        errors.name ? 'border-red-500 bg-red-100' : ''
                      }`}
                    />
                    {errors.name && (
                      <div className="bg-red-400 border-2 border-black p-3 transform rotate-1">
                        <p className="font-black text-black text-sm">{errors.name}</p>
                      </div>
                    )}
                  </div>

                  {/* Event Type Selection */}
                  <div className="space-y-4">
                    <Label className="text-xl font-black text-black uppercase tracking-wide">
                      EVENT TYPE
                    </Label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {eventTypes.map((type, index) => {
                        const IconComponent = type.icon
                        const isSelected = formData.eventType === type.value
                        const rotation = index % 2 === 0 ? 'rotate-1' : '-rotate-1'

                        return (
                          <div
                            key={type.value}
                            className={`${type.color} border-4 border-black p-4 cursor-pointer transition-all duration-200 transform hover:rotate-0 ${
                              isSelected ? 'rotate-0 scale-105' : rotation
                            }`}
                            onClick={() => updateFormData('eventType', type.value)}
                          >
                            <div className="text-center space-y-2">
                              <div className="bg-black p-2 inline-block border-2 border-black">
                                <IconComponent className="w-6 h-6 text-white" />
                              </div>
                              <p className="font-black text-black text-sm uppercase">
                                {type.label}
                              </p>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                    {errors.eventType && (
                      <div className="bg-red-400 border-2 border-black p-3 transform -rotate-1">
                        <p className="font-black text-black text-sm">{errors.eventType}</p>
                      </div>
                    )}
                  </div>

                  {/* Event Description */}
                  <div className="space-y-3">
                    <Label htmlFor="description" className="text-xl font-black text-black uppercase tracking-wide">
                      EVENT DESCRIPTION
                    </Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => updateFormData('description', e.target.value)}
                      placeholder="Tell people what makes your event special! What will they experience? What should they expect?"
                      className={`border-4 border-black bg-white font-bold text-black placeholder:text-black/60 focus:bg-[#facc15] min-h-[120px] ${
                        errors.description ? 'border-red-500 bg-red-100' : ''
                      }`}
                      rows={4}
                    />
                    {errors.description && (
                      <div className="bg-red-400 border-2 border-black p-3 transform rotate-1">
                        <p className="font-black text-black text-sm">{errors.description}</p>
                      </div>
                    )}
                  </div>

                  {/* Date and Time */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-3">
                      <Label htmlFor="date" className="text-xl font-black text-black uppercase tracking-wide">
                        EVENT DATE
                      </Label>
                      <Input
                        id="date"
                        type="date"
                        value={formData.date}
                        min={today}
                        onChange={(e) => updateFormData('date', e.target.value)}
                        className={`border-4 border-black bg-white font-bold text-black focus:bg-[#facc15] text-lg p-4 h-14 ${
                          errors.date ? 'border-red-500 bg-red-100' : ''
                        }`}
                      />
                      {errors.date && (
                        <div className="bg-red-400 border-2 border-black p-3 transform -rotate-1">
                          <p className="font-black text-black text-sm">{errors.date}</p>
                        </div>
                      )}
                    </div>

                    <div className="space-y-3">
                      <Label htmlFor="time" className="text-xl font-black text-black uppercase tracking-wide">
                        EVENT TIME
                      </Label>
                      <Input
                        id="time"
                        type="time"
                        value={formData.time}
                        onChange={(e) => updateFormData('time', e.target.value)}
                        className={`border-4 border-black bg-white font-bold text-black focus:bg-[#facc15] text-lg p-4 h-14 ${
                          errors.time ? 'border-red-500 bg-red-100' : ''
                        }`}
                      />
                      {errors.time && (
                        <div className="bg-red-400 border-2 border-black p-3 transform rotate-1">
                          <p className="font-black text-black text-sm">{errors.time}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Location and Capacity */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-3">
                      <Label htmlFor="location" className="text-xl font-black text-black uppercase tracking-wide">
                        LOCATION
                      </Label>
                      <Input
                        id="location"
                        value={formData.location}
                        onChange={(e) => updateFormData('location', e.target.value)}
                        placeholder="123 Main St, City or Virtual Event"
                        className={`border-4 border-black bg-white font-bold text-black placeholder:text-black/60 focus:bg-[#facc15] text-lg p-4 h-14 ${
                          errors.location ? 'border-red-500 bg-red-100' : ''
                        }`}
                      />
                      {errors.location && (
                        <div className="bg-red-400 border-2 border-black p-3 transform rotate-1">
                          <p className="font-black text-black text-sm">{errors.location}</p>
                        </div>
                      )}
                    </div>

                    <div className="space-y-3">
                      <Label htmlFor="maxAttendees" className="text-xl font-black text-black uppercase tracking-wide">
                        MAX ATTENDEES
                      </Label>
                      <Input
                        id="maxAttendees"
                        type="number"
                        min="1"
                        max="1000"
                        value={formData.maxAttendees}
                        onChange={(e) => updateFormData('maxAttendees', e.target.value)}
                        className={`border-4 border-black bg-white font-bold text-black focus:bg-[#facc15] text-lg p-4 h-14 ${
                          errors.maxAttendees ? 'border-red-500 bg-red-100' : ''
                        }`}
                      />
                      {errors.maxAttendees && (
                        <div className="bg-red-400 border-2 border-black p-3 transform -rotate-1">
                          <p className="font-black text-black text-sm">{errors.maxAttendees}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row gap-4 pt-8">
                    <Button
                      onClick={handleCreateEvent}
                      disabled={isLoading}
                      className="flex-1 bg-black border-4 border-black text-white font-black text-xl uppercase tracking-wide p-6 h-16 transform rotate-2 hover:rotate-0 hover:bg-[#1e74e6] transition-all duration-200"
                    >
                      {isLoading ? (
                        <div className="flex items-center gap-2">
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          CREATING...
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <Zap className="w-6 h-6" />
                          CREATE MY FIRST EVENT!
                        </div>
                      )}
                    </Button>

                    <Link href="/dashboard" className="flex-shrink-0">
                      <Button
                        variant="outline"
                        className="w-full sm:w-auto bg-white border-4 border-black text-black font-black text-lg uppercase tracking-wide p-6 h-16 transform -rotate-1 hover:rotate-0 hover:bg-[#facc15] transition-all duration-200"
                      >
                        SKIP FOR NOW
                      </Button>
                    </Link>
                  </div>

                  {/* Help Text */}
                  <div className="bg-[#60a5fa] border-4 border-black p-6 transform rotate-1">
                    <div className="flex items-start gap-4">
                      <div className="bg-black p-2 border-2 border-black">
                        <CheckCircle className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="font-black text-black text-lg uppercase mb-2">
                          DON'T WORRY - YOU CAN CHANGE EVERYTHING LATER!
                        </h3>
                        <p className="font-bold text-black">
                          This is just to get you started. You can edit all details, add games,
                          set up networking features, and customize everything from your dashboard.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}
